import { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FRAMEWORK_ITEMS = [
  "Strategic Direction & Enterprise Alignment",
  "Operating Model & Performance Architecture",
  "Leadership & Talent Enablement",
  "Strategy Implementation & Measurable Outcomes",
  "Governance, Stakeholder & Regulatory Alignment",
];

const initialForm = {
  name: "",
  email: "",
  organization: "",
  phone: "",
  message: "",
};

export default function LandingPage() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 5)
      e.message = "Please share a few details (5+ characters)";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        phone: form.phone.trim() || null,
        message: form.message.trim(),
      };
      await axios.post(`${API}/contact`, payload);
      toast.success("Thank you. Your message has been received.", {
        description: "Helena will be in touch shortly.",
      });
      setForm(initialForm);
      setOpen(false);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again or email Helena directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="landing-page"
      className="font-sans antialiased min-h-screen flex flex-col items-center justify-center p-4 sm:p-8 md:p-12 bg-espresso-900 text-[#F8F5F2] relative"
    >
      {/* Subtle texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4af37' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")",
        }}
      />

      <Toaster
        position="top-center"
        theme="dark"
        toastOptions={{
          style: {
            background: "#2A1B14",
            border: "1px solid rgba(212,175,55,0.35)",
            color: "#F8F5F2",
          },
        }}
      />

      <div className="relative z-10 w-full max-w-4xl mx-auto opacity-0 animate-fade-in-up">
        <main className="bg-espresso-800/90 backdrop-blur-sm shadow-2xl rounded-sm border border-gold/20 p-8 sm:p-12 md:p-16 lg:p-20 relative overflow-hidden">
          {/* Top gold accent */}
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-espresso-800 via-gold to-espresso-800" />

          {/* Header / Logo */}
          <header className="text-center mb-12">
            <div className="flex justify-center mb-10 md:mb-14">
              <img
                data-testid="brand-logo"
                src="http://dondrae.com/wp-content/uploads/2026/05/H2C2-Logo1-Edited.png"
                alt="H²C² Consulting Group"
                className="h-40 sm:h-48 md:h-56 w-auto object-contain mix-blend-screen"
                style={{ filter: "invert(1) hue-rotate(180deg) brightness(1.2)" }}
              />
            </div>
            <h2 className="font-serif text-gold text-2xl md:text-3xl tracking-[0.2em] uppercase font-medium mt-6">
              Strategy. Development. Execution.
            </h2>
          </header>

          <hr className="gold-separator w-3/4 mx-auto mb-12" />

          {/* Intro */}
          <section className="space-y-8 text-center md:text-left mb-16">
            <h1 className="font-serif text-2xl md:text-3xl lg:text-4xl text-white leading-tight font-semibold text-center">
              Credibility and Consulting <br />
              <span className="text-gold font-light mr-2">&mdash;</span> Linked to Community
            </h1>

            <div className="space-y-6 text-base md:text-lg lg:text-xl font-light text-gray-200 leading-relaxed max-w-3xl mx-auto">
              <p>
                <strong className="font-semibold text-gold">H²C² Consulting Group</strong>{" "}
                partners with public, private, and mission-driven organizations to align
                strategy, strengthen performance, and deliver measurable outcomes across
                complex environments.
              </p>
              <p>
                Led by{" "}
                <strong className="font-semibold text-gold">
                  Helena Haynes-Carter, Managing Director
                </strong>
                , a strategic executive with 20+ years of experience driving performance,
                alignment, and measurable outcomes across corporate, public, and community
                sectors.
              </p>
            </div>
          </section>

          <hr className="gold-separator w-full mb-16" />

          {/* Framework */}
          <section className="mb-16 bg-espresso-900/50 p-8 rounded border border-gold/10">
            <h3 className="font-serif text-2xl md:text-3xl text-gold mb-4 text-center md:text-left font-medium">
              H²C² Strategy-to-Execution Framework
            </h3>
            <p className="font-light italic text-gray-300 mb-8 text-center md:text-left">
              Experience spanning corporate, public sector, and community-based organizations
              &mdash; delivering results across complex, multi-stakeholder environments.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 font-light text-base md:text-lg text-gray-100">
              {FRAMEWORK_ITEMS.slice(0, 4).map((item) => (
                <div key={item} className="flex items-start">
                  <i className="ph-fill ph-diamond text-gold text-[10px] mt-2 mr-4 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
              <div className="md:col-span-2 flex md:justify-center">
                <div className="flex items-start text-left">
                  <i className="ph-fill ph-diamond text-gold text-[10px] mt-2 mr-4 flex-shrink-0" />
                  <span>{FRAMEWORK_ITEMS[4]}</span>
                </div>
              </div>
            </div>
          </section>

          <hr className="gold-separator w-full mb-12" />

          {/* Footer + CTA */}
          <footer className="text-center space-y-10">
            <div className="space-y-4">
              <p className="font-serif text-xl md:text-2xl text-gold italic tracking-wide">
                Strategy, Leadership & Policy{" "}
                <span className="text-white font-light mx-2">&mdash;</span> Across
                Organizations, Communities & Systems
              </p>
              <p className="font-light text-gray-400 text-sm md:text-base">
                Select engagements underway—full platform coming soon.
              </p>
            </div>

            <div>
              <button
                type="button"
                data-testid="open-contact-form-btn"
                onClick={() => setOpen(true)}
                className="inline-block bg-transparent border border-gold text-gold hover:bg-gold hover:text-espresso-900 transition-all duration-300 ease-in-out px-8 py-4 uppercase tracking-[0.15em] text-sm md:text-base font-semibold rounded shadow-[0_0_15px_rgba(212,175,55,0.1)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)] cursor-pointer"
              >
                Request a Conversation
              </button>
            </div>

            {/* Contact details */}
            <div className="flex flex-col items-center justify-center space-y-5 pt-6 font-light text-sm md:text-base text-gray-300">
              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <a
                  data-testid="contact-email-link"
                  href="mailto:Helena@H2C2Group.com"
                  className="group flex items-center hover:text-gold transition-colors duration-300"
                >
                  <i className="ph ph-envelope-simple text-xl mr-2 text-gold group-hover:scale-110 transition-transform duration-300" />
                  Helena@H2C2Group.com
                </a>
                <a
                  data-testid="contact-phone-link"
                  href="tel:7204729242"
                  className="group flex items-center hover:text-gold transition-colors duration-300"
                >
                  <i className="ph ph-phone text-xl mr-2 text-gold group-hover:scale-110 transition-transform duration-300" />
                  720-472-9242
                </a>
                <a
                  data-testid="contact-website-link"
                  href="https://www.h2c2group.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center hover:text-gold transition-colors duration-300"
                >
                  <i className="ph ph-globe text-xl mr-2 text-gold group-hover:scale-110 transition-transform duration-300" />
                  www.H2c2Group.com
                </a>
              </div>

              <div className="flex flex-col md:flex-row items-center gap-4 md:gap-8">
                <div className="flex items-center">
                  <i className="ph ph-map-pin text-xl mr-2 text-gold" />
                  PO Box 440962 Aurora, CO 80014
                </div>
                <a
                  data-testid="contact-linkedin-link"
                  href="https://www.linkedin.com/in/helenahaynescarter"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group flex items-center hover:text-gold transition-colors duration-300"
                >
                  <i className="ph ph-linkedin-logo text-xl mr-2 text-gold group-hover:scale-110 transition-transform duration-300" />
                  Helena Haynes-Carter, Managing Director
                </a>
              </div>
            </div>
          </footer>
        </main>
      </div>

      {/* Contact Form Modal */}
      {open && (
        <div
          data-testid="contact-modal"
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-in"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setOpen(false);
          }}
        >
          <div className="relative w-full max-w-lg bg-espresso-800 border border-gold/30 rounded-sm shadow-2xl p-6 sm:p-10">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-espresso-800 via-gold to-espresso-800" />
            <button
              type="button"
              data-testid="close-contact-form-btn"
              onClick={() => setOpen(false)}
              className="absolute top-3 right-4 text-gray-400 hover:text-gold transition-colors text-2xl leading-none"
              aria-label="Close contact form"
            >
              ×
            </button>

            <div className="mb-6">
              <h3 className="font-serif text-2xl md:text-3xl text-gold font-medium">
                Request a Conversation
              </h3>
              <p className="mt-2 font-light text-gray-300 text-sm md:text-base">
                Share a few details and Helena will follow up personally.
              </p>
            </div>

            <form onSubmit={onSubmit} className="space-y-4" noValidate>
              <Field
                label="Name"
                name="name"
                value={form.name}
                onChange={onChange}
                error={errors.name}
                required
                testId="contact-name-input"
              />
              <Field
                label="Email"
                name="email"
                type="email"
                value={form.email}
                onChange={onChange}
                error={errors.email}
                required
                testId="contact-email-input"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Organization"
                  name="organization"
                  value={form.organization}
                  onChange={onChange}
                  testId="contact-organization-input"
                />
                <Field
                  label="Phone"
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  testId="contact-phone-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.18em] text-gold/80 mb-2">
                  How can we help?<span className="text-gold ml-1">*</span>
                </label>
                <textarea
                  data-testid="contact-message-input"
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  className="w-full bg-espresso-900 border border-gold/20 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 text-gray-100 placeholder-gray-500 px-4 py-3 rounded-sm font-light text-sm md:text-base resize-y"
                  placeholder="Briefly describe your organization, goals, and what brought you here."
                />
                {errors.message && (
                  <p data-testid="contact-message-error" className="mt-1 text-xs text-red-300">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3 sm:justify-end">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="px-6 py-3 text-sm uppercase tracking-[0.15em] font-medium text-gray-300 hover:text-gold border border-transparent hover:border-gold/30 rounded-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="contact-submit-btn"
                  disabled={submitting}
                  className="px-8 py-3 text-sm uppercase tracking-[0.15em] font-semibold border border-gold text-gold hover:bg-gold hover:text-espresso-900 disabled:opacity-60 disabled:cursor-not-allowed rounded-sm transition-all shadow-[0_0_15px_rgba(212,175,55,0.1)] hover:shadow-[0_0_25px_rgba(212,175,55,0.4)]"
                >
                  {submitting ? "Sending…" : "Send Message"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, name, value, onChange, error, type = "text", required, testId }) {
  return (
    <div>
      <label className="block text-xs uppercase tracking-[0.18em] text-gold/80 mb-2">
        {label}
        {required && <span className="text-gold ml-1">*</span>}
      </label>
      <input
        data-testid={testId}
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        className="w-full bg-espresso-900 border border-gold/20 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 text-gray-100 placeholder-gray-500 px-4 py-3 rounded-sm font-light text-sm md:text-base"
      />
      {error && (
        <p data-testid={`${testId}-error`} className="mt-1 text-xs text-red-300">
          {error}
        </p>
      )}
    </div>
  );
}
